/**
 * Subida de archivos desde el navegador.
 *
 * COMPRESIÓN ANTES DE SUBIR
 * -------------------------
 * Una foto de móvil actual pesa 3-6 MB y mide 4000 px de ancho. Para lo que
 * hace esta aplicación —ver el estado de un neumático o un golpe en la
 * carrocería— 1600 px sobran, y el archivo baja a 200-500 KB. Eso importa
 * porque quien inspecciona está en el patio, con datos móviles: subir 5 MB por
 * foto es la diferencia entre que el formulario se guarde o se quede colgado.
 *
 * Se hace con canvas, sin dependencias. `imageOrientation: 'from-image'` aplica
 * la orientación EXIF al dibujar: sin eso, las fotos hechas en vertical con
 * algunos móviles aparecen giradas 90°, que es el defecto clásico de comprimir
 * con canvas.
 *
 * El resultado siempre sale como JPEG. De paso resuelve el HEIC del iPhone:
 * el navegador lo decodifica y lo que viaja es un JPEG que cualquier
 * navegador puede mostrar después.
 *
 * Si algo falla (formato que el navegador no sabe decodificar, canvas sin
 * contexto), se sube el archivo ORIGINAL. Perder calidad es preferible a
 * perder la foto.
 */
import { ApiError } from './api'

export interface CompressOptions {
  /** Lado mayor máximo, en píxeles. */
  maxDimension?: number
  /** Calidad JPEG, 0-1. */
  quality?: number
}

const DEFAULTS: Required<CompressOptions> = { maxDimension: 1600, quality: 0.82 }

export async function compressImage(file: File, options: CompressOptions = {}): Promise<Blob> {
  const { maxDimension, quality } = { ...DEFAULTS, ...options }

  if (!file.type.startsWith('image/')) return file
  // Un PDF o un archivo ya pequeño no gana nada pasando por canvas.
  if (file.size <= 300 * 1024) return file

  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
    const ratio = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height))
    const width = Math.round(bitmap.width * ratio)
    const height = Math.round(bitmap.height * ratio)

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) return file
    ctx.drawImage(bitmap, 0, 0, width, height)
    bitmap.close()

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', quality),
    )
    // Si comprimir no mejora nada (una imagen ya optimizada), se queda el original.
    if (!blob || blob.size >= file.size) return file
    return blob
  } catch {
    return file
  }
}

export type AttachableType =
  | 'vehicle'
  | 'inspection'
  | 'inspection_item'
  | 'work_order'
  | 'expense'
  | 'component'

export interface UploadedDocument {
  id: string
  attachableType: AttachableType
  attachableId: string
  fileName: string
  mimeType: string
  sizeBytes: number
  createdAt: string
  /** URL con la que pintar la imagen. Requiere sesión. */
  url: string
}

export interface UploadParams {
  file: File
  attachableType: AttachableType
  attachableId: string
  /**
   * Columna de conveniencia donde escribir la URL además de registrar el
   * documento: 'primary_photo_url', 'general_photo_url', 'photo_url'…
   */
  field?: string
  description?: string
  /** Por defecto sí: comprime si es imagen. */
  compress?: boolean
}

/**
 * Sube UN archivo. El cuerpo son los bytes crudos y los metadatos van en la
 * query: el endpoint no espera multipart (ver api/uploads/index.ts).
 */
export async function uploadDocument(params: UploadParams): Promise<UploadedDocument> {
  const { file, attachableType, attachableId, field, description, compress = true } = params

  const blob = compress ? await compressImage(file) : file
  // Si se comprimió, el nombre original ya no describe el contenido (era .heic
  // y ahora es JPEG). Se ajusta la extensión para que la descarga posterior
  // abra con el programa correcto.
  const name =
    blob !== file && blob.type === 'image/jpeg'
      ? file.name.replace(/\.[^.]+$/, '') + '.jpg'
      : file.name

  const query = new URLSearchParams({ attachableType, attachableId, fileName: name })
  if (field) query.set('field', field)
  if (description) query.set('description', description)

  let res: Response
  try {
    res = await fetch(`/api/uploads?${query.toString()}`, {
      method: 'POST',
      credentials: 'same-origin',
      // El navegador NO debe añadir charset ni convertir nada: son bytes.
      headers: { 'Content-Type': blob.type || 'application/octet-stream' },
      body: blob,
    })
  } catch {
    throw new ApiError(0, 'No se pudo subir el archivo. Revisa la conexión.', 'NETWORK')
  }

  const text = await res.text()
  const payload: unknown = text ? JSON.parse(text) : null

  if (!res.ok) {
    const err = (payload ?? {}) as { error?: string; code?: string }
    throw new ApiError(res.status, err.error ?? `Error ${res.status}`, err.code)
  }

  return (payload as { document: UploadedDocument }).document
}
